/**
 * ============================================================================
 * Excel Export Utilities for Client Emissions
 * ============================================================================
 * 
 * Generates detailed Excel reports with activity indicators, emission factors,
 * and carbon tonnage for client emissions analysis.
 * 
 * Features:
 * - Multi-sheet workbook with summary and detailed data
 * - Professional formatting with headers, colors, and borders
 * - Automatic column width adjustment
 * - Support for multiple languages (i18n ready)
 * 
 * ============================================================================
 */

import ExcelJS from 'exceljs';

export interface EmissionDataRow {
  id: number;
  category: string;
  subcategory?: string;
  processName: string;
  scope: string;
  activityValue: number;
  unit: string;
  feValue: number;
  tco2e: number;
  createdAt: string;
  notes?: string;
}

export interface EmissionExportData {
  companyName: string;
  periodLabel: string;
  year: number;
  startDate?: string;
  endDate?: string;
  emissions: EmissionDataRow[];
  totalEmissions: number;
  scope1: number;
  scope2: number;
  scope3: number;
}

/**
 * Generate and download an Excel file with emission data
 */
export async function exportEmissionsToExcel(data: EmissionExportData, filename?: string) {
  const workbook = new ExcelJS.Workbook();
  
  // Add summary sheet
  addSummarySheet(workbook, data);
  
  // Add detailed data sheet
  addDetailedDataSheet(workbook, data);
  
  // Add scope breakdown sheet
  addScopeBreakdownSheet(workbook, data);
  
  // Generate and download
  const buffer = await workbook.xlsx.writeBuffer();
  downloadFile(
    buffer,
    filename || `emissions-${data.companyName}-${data.periodLabel}-${new Date().toISOString().split('T')[0]}.xlsx`
  );
}

/**
 * Add summary sheet with key metrics
 */
function addSummarySheet(workbook: ExcelJS.Workbook, data: EmissionExportData) {
  const sheet = workbook.addWorksheet('Résumé', { properties: { tabColor: 'FF4472C4' } });
  
  // Title
  sheet.mergeCells('A1:D1');
  const titleCell = sheet.getCell('A1');
  titleCell.value = 'Rapport d\'Émissions Carbone';
  titleCell.font = { size: 16, bold: true, color: { argb: 'FF1e3a5f' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'center' };
  sheet.getRow(1).height = 25;
  
  // Company and period info
  sheet.getCell('A3').value = 'Entreprise:';
  sheet.getCell('A3').font = { bold: true };
  sheet.getCell('B3').value = data.companyName;
  
  sheet.getCell('A4').value = 'Période:';
  sheet.getCell('A4').font = { bold: true };
  sheet.getCell('B4').value = data.periodLabel;
  
  sheet.getCell('A5').value = 'Année:';
  sheet.getCell('A5').font = { bold: true };
  sheet.getCell('B5').value = data.year;
  
  if (data.startDate) {
    sheet.getCell('A6').value = 'Date de début:';
    sheet.getCell('A6').font = { bold: true };
    sheet.getCell('B6').value = new Date(data.startDate).toLocaleDateString('fr-FR');
  }
  
  if (data.endDate) {
    sheet.getCell('A7').value = 'Date de fin:';
    sheet.getCell('A7').font = { bold: true };
    sheet.getCell('B7').value = new Date(data.endDate).toLocaleDateString('fr-FR');
  }
  
  // Summary metrics
  const metricsRow = data.endDate ? 9 : 8;
  
  sheet.mergeCells(`A${metricsRow}:D${metricsRow}`);
  const metricsTitle = sheet.getCell(`A${metricsRow}`);
  metricsTitle.value = 'Résumé des Émissions';
  metricsTitle.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
  metricsTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
  metricsTitle.alignment = { horizontal: 'center', vertical: 'center' };
  sheet.getRow(metricsRow).height = 20;
  
  // Headers for metrics
  const headerRow = metricsRow + 1;
  const headers = ['Métrique', 'Valeur (tCO₂e)', 'Pourcentage', 'Unité'];
  headers.forEach((header, idx) => {
    const cell = sheet.getCell(headerRow, idx + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
    cell.alignment = { horizontal: 'center', vertical: 'center' };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  });
  
  // Metrics data
  const metrics = [
    ['Émissions Totales', data.totalEmissions, 100, 'tCO₂e'],
    ['Scope 1', data.scope1, (data.scope1 / data.totalEmissions * 100).toFixed(1), 'tCO₂e'],
    ['Scope 2', data.scope2, (data.scope2 / data.totalEmissions * 100).toFixed(1), 'tCO₂e'],
    ['Scope 3', data.scope3, (data.scope3 / data.totalEmissions * 100).toFixed(1), 'tCO₂e'],
  ];
  
  metrics.forEach((metric, idx) => {
    const row = headerRow + 1 + idx;
    metric.forEach((value, colIdx) => {
      const cell = sheet.getCell(row, colIdx + 1);
      cell.value = value;
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      
      if (colIdx === 1 || colIdx === 2) {
        cell.numFmt = colIdx === 1 ? '0.00' : '0.0';
        cell.alignment = { horizontal: 'right' };
      }
    });
  });
  
  // Set column widths
  sheet.columns = [
    { width: 25 },
    { width: 20 },
    { width: 15 },
    { width: 15 },
  ];
  
  // Generated date
  const dateRow = headerRow + 6;
  sheet.getCell(`A${dateRow}`).value = `Généré le: ${new Date().toLocaleString('fr-FR')}`;
  sheet.getCell(`A${dateRow}`).font = { italic: true, size: 10, color: { argb: 'FF666666' } };
}

/**
 * Add detailed data sheet with all emission entries
 */
function addDetailedDataSheet(workbook: ExcelJS.Workbook, data: EmissionExportData) {
  const sheet = workbook.addWorksheet('Données Détaillées', { properties: { tabColor: 'FF70AD47' } });
  
  // Title
  sheet.mergeCells('A1:H1');
  const titleCell = sheet.getCell('A1');
  titleCell.value = 'Détail des Émissions';
  titleCell.font = { size: 14, bold: true, color: { argb: 'FF1e3a5f' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'center' };
  sheet.getRow(1).height = 22;
  
  // Headers
  const headers = [
    'Catégorie',
    'Sous-catégorie',
    'Processus',
    'Scope',
    'Indicateur d\'activité',
    'Unité',
    'Facteur d\'émission',
    'tCO₂e',
    'Notes',
    'Date de création',
  ];
  
  const headerRow = 3;
  headers.forEach((header, idx) => {
    const cell = sheet.getCell(headerRow, idx + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF70AD47' } };
    cell.alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  });
  sheet.getRow(headerRow).height = 30;
  
  // Data rows
  data.emissions.forEach((emission, idx) => {
    const row = headerRow + 1 + idx;
    const rowData = [
      emission.category,
      emission.subcategory || '-',
      emission.processName,
      emission.scope,
      emission.activityValue,
      emission.unit,
      emission.feValue,
      emission.tco2e,
      emission.notes || '-',
      new Date(emission.createdAt).toLocaleDateString('fr-FR'),
    ];
    
    rowData.forEach((value, colIdx) => {
      const cell = sheet.getCell(row, colIdx + 1);
      cell.value = value;
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        right: { style: 'thin', color: { argb: 'FFD3D3D3' } },
      };
      
      // Alternate row colors
      if (idx % 2 === 0) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
      }
      
      // Number formatting
      if (colIdx === 4 || colIdx === 6 || colIdx === 7) {
        cell.numFmt = '0.00';
        cell.alignment = { horizontal: 'right' };
      } else if (colIdx === 9) {
        cell.alignment = { horizontal: 'center' };
      } else {
        cell.alignment = { horizontal: 'left', wrapText: true };
      }
    });
  });
  
  // Set column widths
  sheet.columns = [
    { width: 18 },
    { width: 18 },
    { width: 25 },
    { width: 10 },
    { width: 18 },
    { width: 12 },
    { width: 18 },
    { width: 12 },
    { width: 20 },
    { width: 15 },
  ];
  
  // Freeze header row
  sheet.views = [{ state: 'frozen', ySplit: 3 }];
}

/**
 * Add scope breakdown sheet
 */
function addScopeBreakdownSheet(workbook: ExcelJS.Workbook, data: EmissionExportData) {
  const sheet = workbook.addWorksheet('Répartition Scope', { properties: { tabColor: 'FFFFC000' } });
  
  // Title
  sheet.mergeCells('A1:C1');
  const titleCell = sheet.getCell('A1');
  titleCell.value = 'Répartition par Scope';
  titleCell.font = { size: 14, bold: true, color: { argb: 'FF1e3a5f' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'center' };
  sheet.getRow(1).height = 22;
  
  // Summary by scope
  const scopeData = data.emissions.reduce((acc, emission) => {
    const scope = emission.scope || 'Scope 3';
    if (!acc[scope]) {
      acc[scope] = { count: 0, total: 0 };
    }
    acc[scope].count += 1;
    acc[scope].total += emission.tco2e;
    return acc;
  }, {} as Record<string, { count: number; total: number }>);
  
  // Headers
  const headerRow = 3;
  const headers = ['Scope', 'Nombre d\'entrées', 'tCO₂e Total', 'Pourcentage'];
  headers.forEach((header, idx) => {
    const cell = sheet.getCell(headerRow, idx + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC000' } };
    cell.alignment = { horizontal: 'center', vertical: 'center' };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  });
  
  // Data rows
  const scopes = ['Scope 1', 'Scope 2', 'Scope 3'];
  scopes.forEach((scope, idx) => {
    const row = headerRow + 1 + idx;
    const scopeInfo = scopeData[scope] || { count: 0, total: 0 };
    const percentage = data.totalEmissions > 0 ? (scopeInfo.total / data.totalEmissions * 100) : 0;
    
    const rowData = [
      scope,
      scopeInfo.count,
      scopeInfo.total,
      percentage,
    ];
    
    rowData.forEach((value, colIdx) => {
      const cell = sheet.getCell(row, colIdx + 1);
      cell.value = value;
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      
      if (colIdx === 1) {
        cell.alignment = { horizontal: 'center' };
      } else if (colIdx === 2 || colIdx === 3) {
        cell.numFmt = colIdx === 2 ? '0.00' : '0.0';
        cell.alignment = { horizontal: 'right' };
      }
    });
  });
  
  // Category breakdown
  const categoryRow = headerRow + 5;
  sheet.mergeCells(`A${categoryRow}:C${categoryRow}`);
  const categoryTitle = sheet.getCell(`A${categoryRow}`);
  categoryTitle.value = 'Répartition par Catégorie';
  categoryTitle.font = { size: 12, bold: true, color: { argb: 'FF1e3a5f' } };
  
  // Category headers
  const catHeaderRow = categoryRow + 1;
  const catHeaders = ['Catégorie', 'Nombre d\'entrées', 'tCO₂e Total'];
  catHeaders.forEach((header, idx) => {
    const cell = sheet.getCell(catHeaderRow, idx + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
    cell.alignment = { horizontal: 'center', vertical: 'center' };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  });
  
  // Category data
  const categoryData = data.emissions.reduce((acc, emission) => {
    const category = emission.category || 'Autre';
    if (!acc[category]) {
      acc[category] = { count: 0, total: 0 };
    }
    acc[category].count += 1;
    acc[category].total += emission.tco2e;
    return acc;
  }, {} as Record<string, { count: number; total: number }>);
  
  Object.entries(categoryData).forEach(([category, info], idx) => {
    const row = catHeaderRow + 1 + idx;
    const rowData = [category, info.count, info.total];
    
    rowData.forEach((value, colIdx) => {
      const cell = sheet.getCell(row, colIdx + 1);
      cell.value = value;
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
        right: { style: 'thin', color: { argb: 'FFD3D3D3' } },
      };
      
      if (idx % 2 === 0) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
      }
      
      if (colIdx === 1) {
        cell.alignment = { horizontal: 'center' };
      } else if (colIdx === 2) {
        cell.numFmt = '0.00';
        cell.alignment = { horizontal: 'right' };
      }
    });
  });
  
  // Set column widths
  sheet.columns = [
    { width: 25 },
    { width: 18 },
    { width: 18 },
    { width: 15 },
  ];
}

/**
 * Helper function to download file
 */
function downloadFile(buffer: Buffer | Uint8Array, filename: string) {
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
